import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  ReactElement,
} from 'react';

import axios from 'axios';
import { BASE_URL } from '../config/api';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  signUp: (email: string, senha: string, extraData: any) => Promise<void>;
  signIn: (cpf: string, senha: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function safeLocalStorageSet(key: string, value: string) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch {
    console.warn(`localStorage.setItem falhou: ${key}`);
  }
}

function safeLocalStorageRemove(key: string) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch {
    console.warn(`localStorage.removeItem falhou: ${key}`);
  }
}

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: BASE_URL,
  });

  useEffect(() => {
    setLoading(false);
  }, []);

  const signUp = async (email: string, senha: string, extraData: any) => {
    try {
      setLoading(true);
      const payload = {
        ...extraData,
        email,
        senha,
        concordancia: Boolean(extraData.aceite_termos),
        ofertas: Boolean(extraData.aceite_ofertas),
        mensagens: Boolean(extraData.aceite_sms),
      };
      const { data } = await api.post('/club/insert', payload);

      if (!data?.usuario || !data?.clube_token) {
        throw new Error('Não foi possível autorizar o acesso ao clube.');
      }
      enviarTokenClubeBen(data.clube_token);
      setUser(data.usuario);
      safeLocalStorageSet('user', JSON.stringify(data.usuario));
    } catch (error: any) {
      console.error('Erro ao cadastrar usuário:', error);
      throw new Error(
        error.response?.data?.mensagem ||
          error.message ||
          'Erro ao criar conta',
      );
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (cpf: string, senha: string): Promise<void> => {
    try {
      setLoading(true);

      const cpfLimpo = cpf.replace(/\D/g, '');

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        throw new Error('CPF deve ter 11 dígitos');
      }

      const { data } = await api.post('/club/login', { cpf: cpfLimpo, senha });

      if (data?.mensagem && data.sucesso === false) {
        throw new Error(data.mensagem);
      }

      if (!data?.usuario || !data?.clube_token) {
        throw new Error('Não foi possível autorizar o acesso ao clube.');
      }

      const userData = data.usuario;

      enviarTokenClubeBen(data.clube_token);
      setUser(userData);
      safeLocalStorageSet('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('Erro completo no login:', {
        message: error.message,
        response: error.response,
      });

      if (error.response) {
        const status = error.response.status;
        const apiMessage =
          error.response.data?.mensagem || error.response.data?.message;

        if (status === 404) {
          throw new Error(
            'CPF não cadastrado no sistema. Crie sua conta para continuar.',
          );
        }

        if (status === 401) {
          throw new Error('CPF ou senha incorretos. Verifique seus dados.');
        }

        if (apiMessage) throw new Error(apiMessage);

        throw new Error(`Erro ${status}: Não foi possível fazer login`);
      }

      throw new Error(
        error.message || 'Erro ao conectar com o servidor. Tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  const enviarTokenClubeBen = (token: string) => {
    try {
      const clubeUrl = 'https://cuidado.clubeben.com.br/auth/general/';

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = clubeUrl;
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = token;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      form.remove();
    } catch (error: any) {
      throw new Error('Não foi possível abrir o Clube de Benefícios.');
    }
  };

  const signOut = (): void => {
    setUser(null);
    safeLocalStorageRemove('user');
  };

  const value: AuthContextType = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
